import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { showSuccess } from "../../utils/toast.js";
import { useAuth } from "../ui/AuthContext.jsx";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import AppButton from "../ui/AppButton.jsx";
import CreateObjektModal from "../Objekte/CreateObjektModal.jsx";

export default function KundenBlockObjekte({ kundenId, onObjektCreated }) {
  const { roleIdIn } = useAuth();
  const isManager = roleIdIn([1, 2, 3, 4]);
  const navigate = useNavigate();
  const [objekte, setObjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockOpen, setBlockOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(`kunden_objekte_open_${kundenId}`);
      if (stored !== null) return JSON.parse(stored);
    } catch {}
    return false;
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      setLoading(true);
      const res = await api.get(`/objekte/kunde/${kundenId}`, {
        params: { include_archived: "0" },
        signal: ctrl.signal,
      });
      setObjekte(res.data?.items || []);
    } catch (e) {
      if (e?.name === "CanceledError") return;
      setObjekte([]);
    } finally {
      setLoading(false);
    }
  }, [kundenId]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => {
    if (loading || objekte.length === 0) return;
    try {
      const stored = localStorage.getItem(`kunden_objekte_open_${kundenId}`);
      if (stored === null) setBlockOpen(true);
    } catch {}
  }, [loading, objekte.length, kundenId]);

  const handleObjektCreated = useCallback(() => {
    showSuccess("Objekt erstellt");
    setShowCreateModal(false);
    load();
    onObjektCreated?.();
  }, [load, onObjektCreated]);

  const totalProjekte = objekte.reduce((s, o) => s + (o.projekt_count || 0), 0);
  const totalService = objekte.reduce((s, o) => s + (o.service_count || 0), 0);
  const headerRight = `${totalProjekte} Projekte · ${totalService} Aufträge`;
  const headerAction =
    isManager && blockOpen ? (
      <AppButton
        type="button"
        variant="primary"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setShowCreateModal(true);
        }}
        className="inline-flex items-center gap-1.5 shrink-0"
      >
        Neues Objekt
      </AppButton>
    ) : null;

  return (
    <>
      <CollapsibleBlock
        title="🏢 Objekte / Liegenschaften"
        headerInfo={headerRight}
        headerActions={headerAction}
        panelId="kunden-objekte-panel"
        defaultOpen={blockOpen}
        persistKey={`kunden_objekte_open_${kundenId}`}
        contentClassName="px-4 sm:px-6 pb-4 sm:pb-5"
        onToggle={setBlockOpen}
      >
        {loading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-12 rounded-2xl bg-white/10" />
            <div className="h-12 rounded-2xl bg-white/10" />
          </div>
        ) : objekte.length === 0 ? (
          <p className="text-sm text-muted py-2">Noch keine Objekte vorhanden.</p>
        ) : (
          <div className="space-y-2">
            {objekte.map((o) => {
              const addr = [o.adresse, [o.plz, o.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
              const line2 = addr || `${o.projekt_count || 0} Projekte · ${o.service_count || 0} Aufträge`;
              return (
                <div
                  key={o.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Objekt ${o.name || o.id} öffnen`}
                  onClick={() => navigate(`/objekte/${o.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/objekte/${o.id}`);
                    }
                  }}
                  className="group interactive-card cursor-pointer rounded-2xl px-3 py-2.5 border border-default bg-surface hover:!bg-surface transition-all focus:outline-none focus-within:ring-2 focus-within:ring-blue-400/30"
                >
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-center min-w-0">
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-blue-100/90 truncate">{o.name}</div>
                      <div className="text-[12px] text-blue-200/70 truncate mt-0.5">{line2}</div>
                    </div>
                    {addr ? (
                      <span
                        className="text-[11px] text-blue-200/60 whitespace-nowrap tabular-nums shrink-0"
                        title={`${o.projekt_count || 0} Projekte · ${o.service_count || 0} Aufträge`}
                      >
                        {o.projekt_count || 0} · {o.service_count || 0}
                      </span>
                    ) : (
                      <Building2 className="w-4 h-4 text-blue-400/70 shrink-0" aria-hidden />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleBlock>

      {showCreateModal && (
        <CreateObjektModal
          kundeId={kundenId}
          onCreated={handleObjektCreated}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </>
  );
}
